import React from 'react';
import { ConnectedTools, CostDriversTable, useInViewAnim, tweenValueString } from './DashboardWidgets.jsx';
import { IntegrationLogo } from './Integrations.jsx';
import { t as tx } from './i18n/useT.js';

// `anchorHorizon` pins the hero background's light line behind this shell's
// window bar, the same way the sample report hero pins it to its own bar —
// the ref is only attached when the flag is set, so `useHorizonAnchor`
// no-ops (and the CSS viewport clamp wins) for shells that leave it off.
export function HeroWindowShell({ title, meta, children, className = '', anchorHorizon = false }) {
  const barRef = React.useRef(null);
  useHorizonAnchor(barRef);
  return (
    <div className={`sw-hero-window ${className}`.trim()} role="group" aria-label={title}>
      <div className="sw-hero-window-bar" ref={anchorHorizon ? barRef : null}>
        <span className="sw-hero-window-dots" aria-hidden="true"><i/><i/><i/></span>
        <strong>{title}</strong>
        <span>{meta}</span>
      </div>
      <div className="sw-hero-window-body">{children}</div>
    </div>
  );
}

// =====================================================================
// Sample report hero — a client-ready report summary with an evidence
// trail behind it. The "Evidence trail" tab mirrors the Delivery event
// stream from the dashboard prototype's Project forensics view
// (ScopeWorth.Dashboard, forensics-views.jsx → TimelineMode), including
// its kind → flag-tag mapping, so the marketing surface and the product
// tell the same story with the same vocabulary.
// =====================================================================

// Pins the hero background's light line to the top edge of `ref`, so the bloom
// reads as light coming from behind the shell rather than a band floating in
// the gap above it. The measurement is published as `--sw-horizon-anchor` on
// the hero header and read by `.sw-hero-bg--horizon` (styles.css), which falls
// back to its viewport clamp whenever the anchor is absent — no JS, no hero,
// or the sub-980px layout, which deliberately puts the bloom at the hero base.
function useHorizonAnchor(ref) {
  React.useLayoutEffect(() => {
    const el = ref.current;
    const hero = el && el.closest('.sw-page-hero');
    if (!hero) return undefined;
    let raf = 0;
    const measure = () => {
      raf = 0;
      const top = el.getBoundingClientRect().top - hero.getBoundingClientRect().top;
      hero.style.setProperty('--sw-horizon-anchor', `${top.toFixed(1)}px`);
    };
    // The anchor moves whenever the copy rewraps or the shell changes height
    // (tab switch), both of which resize the hero itself.
    const schedule = () => { if (!raf) raf = requestAnimationFrame(measure); };
    measure();
    const ro = new ResizeObserver(schedule);
    ro.observe(hero);
    ro.observe(el);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(schedule);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      hero.style.removeProperty('--sw-horizon-anchor');
    };
  }, [ref]);
}

const SR_FLOW = [
  { key: 'jira',    logo: 'Jira',   label: 'Jira',    sub: 'Work items',  value: '22' },
  { key: 'pr',      logo: 'GitHub', label: 'PR',      sub: 'Merged',      value: '14' },
  { key: 'release', logo: 'GitLab', label: 'Release', sub: 'Shipped',     value: '3' },
  { key: 'invoice', logo: null,     label: 'Invoice', sub: 'Investment',  value: '$38,400' },
];

const SR_KPIS = [
  { value: '22',  label: 'work items', tone: 'blue' },
  { value: '14',  label: 'merged PRs', tone: 'teal' },
  { value: '3',   label: 'releases',   tone: 'amber' },
  { value: '74%', label: 'confidence', tone: 'teal' },
];

const SR_INVESTMENT = [
  { label: 'Feature delivery',        value: '$18,400' },
  { label: 'Bug fixing',              value: '$4,600' },
  { label: 'Rework',                  value: '$3,200', tone: 'amber' },
  { label: 'Meetings & coordination', value: '$5,100' },
  { label: 'Code review',             value: '$3,600' },
  { label: 'Other',                   value: '$3,500' },
];

const SR_AI_ROWS = [
  { label: 'Assumed implementation reduction', value: '21h', tone: 'teal' },
  { label: 'Additional review overhead',    value: '4h',  tone: 'amber' },
  { label: 'Illustrative effort difference', value: '17h', tone: 'teal' },
];

// Mirrors window.SW_DATA.timeline in the dashboard prototype (data.js).
//
// Deliberately six rows, not the prototype's eleven: the evidence trail shares
// its shell with the client view, and a full-length stream made the tab a
// ~300px taller panel, so switching tabs shoved the rest of the page under the
// reader's scroll position. The cut keeps one row of every `kind` — all five
// filter chips still return something — and every flagged, costed event; what
// went was the repetition (three of the four near-identical release rows, the
// second clarification, the second rework).
const SR_EVENTS = [
  { date: 'Apr 02', kind: 'plan',   tag: 'Scope lock',      title: 'Scope baseline locked', desc: '34 work items committed for April delivery.' },
  { date: 'Apr 05', kind: 'add',    tag: 'Scope add',       title: '3 new tickets added after lock', desc: 'Feature: Alert routing (PORT-412, 413, 414).', cost: '+$1,600' },
  { date: 'Apr 09', kind: 'clar',   tag: 'Clarification',   title: 'Acceptance criteria changed on Console preferences', desc: 'AC rewritten on PORT-388. Meeting cluster, 2.0h.' },
  { date: 'Apr 12', kind: 'rework', tag: 'Reopened',        title: '2 tickets reopened after requirement changes', desc: 'PORT-356 and PORT-368 reopened post-merge.', cost: '+$1,800' },
  { date: 'Apr 26', kind: 'add',    tag: 'Scope add',       title: '4 new requests moved into current cycle', desc: 'PORT-431..434 added at client request.', cost: '+$3,800' },
  { date: 'Apr 30', kind: 'ship',   tag: 'Released',        title: 'v2.9 released — Telemetry performance', desc: '8 work items shipped, 6 PRs merged.' },
];

const SR_FILTERS = [
  ['all', 'All'], ['plan', 'Planned'], ['add', 'Added'],
  ['rework', 'Rework'], ['clar', 'Clarification'], ['ship', 'Shipped'],
];

// Same typology flags the prototype stamps on each event kind.
const SR_EVENT_FLAG = {
  plan:   null,
  add:    { label: 'added-after-lock', tone: 'amber' },
  rework: { label: 'reopened-from',    tone: 'red' },
  clar:   { label: 'meeting-overrun',  tone: 'amber' },
  ship:   { label: 'shipped-clean',    tone: 'teal' },
};

function InvoiceMark() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8Z"/>
      <path d="M14 3v5h5"/>
      <path d="M9 13h6M9 17h4"/>
    </svg>
  );
}

function SampleReportClientView({ t }) {
  return (
    <div className="sw-hero-sr-card">
      <div className="sw-hero-sr-head">
        <h3>Northwind Studios · Acme Retail Group · April 2026</h3>
        <p>Engagement: build and maintain · Period: 1 to 30 April 2026 · Confidence: Medium · 74%</p>
      </div>

      <div className="sw-hero-sr-flow">
        {SR_FLOW.map((step, i) => (
          <React.Fragment key={step.key}>
            <div className="sw-hero-sr-step">
              <div className="sw-hero-sr-step-head">
                <span className={`sw-hero-sr-step-mark is-${step.key}`}>
                  {step.logo ? <IntegrationLogo name={step.logo} size={20} onDark/> : <InvoiceMark/>}
                </span>
                <span className="sw-hero-sr-step-copy">
                  <strong>{step.label}</strong>
                  <span>{step.sub}</span>
                </span>
              </div>
              <div className="sw-hero-sr-step-value">{tweenValueString(step.value, t)}</div>
            </div>
            {i < SR_FLOW.length - 1 ? <span className="sw-hero-sr-chevron" aria-hidden="true">›</span> : null}
          </React.Fragment>
        ))}
      </div>

      <div className="sw-hero-sr-kpis">
        {SR_KPIS.map((kpi) => (
          <div className="sw-hero-sr-kpi" key={kpi.label}>
            <div className={`sw-hero-sr-kpi-v is-${kpi.tone}`}>{tweenValueString(kpi.value, t)}</div>
            <div className="sw-hero-sr-kpi-l">{kpi.label}</div>
          </div>
        ))}
      </div>

      <div className="sw-hero-sr-split">
        <section className="sw-hero-sr-panel">
          <div className="sw-hero-sr-panel-h is-teal">Delivery investment</div>
          <ul className="sw-hero-sr-lines">
            {SR_INVESTMENT.map((line) => (
              <li key={line.label}>
                <span>{line.label}</span>
                <span className={`sw-hero-sr-num${line.tone ? ` is-${line.tone}` : ''}`}>{tweenValueString(line.value, t)}</span>
              </li>
            ))}
          </ul>
          <div className="sw-hero-sr-total">
            <span>Total</span>
            <span className="sw-hero-sr-num">{tweenValueString('$38,400', t)}</span>
          </div>
        </section>

        <div className="sw-hero-sr-stack">
          <section className="sw-hero-sr-panel">
            <div className="sw-hero-sr-panel-h is-teal">
              Scope expansion
              <span className="sw-hero-sr-badge is-amber">{tweenValueString('+$6,800', t)}</span>
            </div>
            <p className="sw-hero-sr-note">
              Work delivered after project start that was not part of the agreed scope, including 4 change
              requests and 2 expanded tickets. Each links to its source ticket in the full report.
            </p>
          </section>

          <section className="sw-hero-sr-panel">
            <div className="sw-hero-sr-panel-h is-teal">
              Illustrative AI effort
              <span className="sw-hero-sr-badge is-teal">{tweenValueString('+17h estimate', t)}</span>
            </div>
            <ul className="sw-hero-sr-lines">
              {SR_AI_ROWS.map((row) => (
                <li key={row.label}>
                  <span>{row.label}</span>
                  <span className={`sw-hero-sr-num is-${row.tone}`}>{tweenValueString(row.value, t)}</span>
                </li>
              ))}
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
}

function SampleReportEvidenceTrail() {
  const [filter, setFilter] = React.useState('all');
  const events = filter === 'all' ? SR_EVENTS : SR_EVENTS.filter((e) => e.kind === filter);

  return (
    <div className="sw-hero-sr-card is-stream">
      <div className="sw-hero-sr-stream-head">
        <div>
          <div className="sw-hero-sr-stream-title"><i aria-hidden="true"/>Delivery event stream</div>
          <p>Every merge, ticket, AC change, meeting &amp; release — screened against scope &amp; cost typologies</p>
        </div>
        <div className="sw-hero-sr-toggle" role="group" aria-label="Filter events by type">
          {SR_FILTERS.map(([value, label]) => (
            <button
              type="button"
              key={value}
              className={filter === value ? 'is-on' : ''}
              aria-pressed={filter === value}
              onClick={() => setFilter(value)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="sw-hero-sr-timeline">
        {events.map((ev, i) => {
          const flag = SR_EVENT_FLAG[ev.kind];
          return (
            <div className={`sw-hero-sr-event is-${ev.kind}`} key={`${ev.date}-${i}`}>
              <div className="sw-hero-sr-event-date">{ev.date}</div>
              <div className="sw-hero-sr-event-body">
                <div className="sw-hero-sr-event-title">{ev.title}</div>
                <div className="sw-hero-sr-event-desc">{ev.desc}</div>
              </div>
              <div className="sw-hero-sr-event-meta">
                <span className={`sw-hero-sr-flag${flag ? ` is-${flag.tone}` : ' is-plain'}`}>
                  {flag ? flag.label : ev.tag}
                </span>
                {ev.cost ? <span className="sw-hero-sr-event-cost">{ev.cost}</span> : null}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function SampleReportHeroVisual() {
  const [tab, setTab] = React.useState('client');
  const [ref, t] = useInViewAnim({ duration: 1400 });
  const barRef = React.useRef(null);
  const bodyRef = React.useRef(null);
  const [floor, setFloor] = React.useState(0);
  useHorizonAnchor(barRef);

  // The client view — the tab that opens — sets the shell's floor height, and
  // the evidence trail is trimmed to sit just inside it. Together that pins the
  // panel: neither switching tabs nor filtering the trail down to a single row
  // resizes the hero, so the page never moves under the reader's scroll.
  React.useLayoutEffect(() => {
    const el = bodyRef.current;
    if (!el || tab !== 'client') return undefined;
    const measure = () => setFloor(Math.round(el.getBoundingClientRect().height));
    measure();
    // Catches the copy rewrapping at a new width and the reflow after webfonts
    // swap in; setting min-height to the height we just read is a no-op, so
    // this cannot feed itself.
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [tab]);

  return (
    <div className="sw-hero-sr" ref={ref}>
      <div className="sw-hero-sr-bar" ref={barRef}>
        <div className="sw-hero-sr-tabs" role="tablist" aria-label="Sample report view">
          <button
            type="button"
            role="tab"
            id="sw-hero-sr-tab-client"
            aria-selected={tab === 'client'}
            aria-controls="sw-hero-sr-panel"
            className={tab === 'client' ? 'is-on' : ''}
            onClick={() => setTab('client')}
          >
            Client view
          </button>
          <button
            type="button"
            role="tab"
            id="sw-hero-sr-tab-evidence"
            aria-selected={tab === 'evidence'}
            aria-controls="sw-hero-sr-panel"
            className={tab === 'evidence' ? 'is-on' : ''}
            onClick={() => setTab('evidence')}
          >
            Evidence trail
          </button>
        </div>
        <span className="sw-hero-sr-stamp">Client-ready</span>
      </div>

      <div
        className="sw-hero-sr-body"
        id="sw-hero-sr-panel"
        role="tabpanel"
        ref={bodyRef}
        style={floor ? { minHeight: floor } : undefined}
        aria-labelledby={tab === 'client' ? 'sw-hero-sr-tab-client' : 'sw-hero-sr-tab-evidence'}
      >
        {tab === 'client' ? <SampleReportClientView t={t}/> : <SampleReportEvidenceTrail/>}
      </div>
    </div>
  );
}

export function UseCasesEvidenceHeroVisual() {
  return (
    <HeroWindowShell title="Delivery Investment · Evidence behind every line" meta="Last month · 4 evidence rows" anchorHorizon>
      <div className="sw-hero-widget-panel sw-hero-evidence-panel">
        <CostDriversTable/>
      </div>
    </HeroWindowShell>
  );
}

export function IntegrationsToolsHeroVisual() {
  return (
    <HeroWindowShell title="Your workspace sources" meta="Read-only · Metadata-first">
      <div className="sw-hero-widget-panel sw-hero-tools-panel">
        <ConnectedTools/>
      </div>
    </HeroWindowShell>
  );
}

const EARLY_ACCESS_TOOLS = [
  ['Jira', true], ['Linear', false], ['Azure DevOps', false], ['GitHub', true], ['GitLab', false],
  ['Bitbucket', false], ['Google Calendar', true], ['Microsoft 365', false], ['GitHub Copilot', true], ['Cursor', false],
  ['Claude Code', false], ['AWS', false], ['Google Cloud', false], ['Microsoft Azure', false], ['Slack', false],
  ['Microsoft Teams', false],
];

function ToolMark({ name }) {
  return (
    <span className="sw-hero-tool-mark" aria-hidden="true">
      <IntegrationLogo name={name} size={20} onDark/>
      <span className="sw-hero-tool-monogram">{name.slice(0, 1)}</span>
    </span>
  );
}

export function EarlyAccessStackHeroVisual() {
  // Cosmetic only — toggling a card never connects anything (the summary line
  // below says as much); it just plays with the selection and the counter.
  const [selected, setSelected] = React.useState(
    () => new Set(EARLY_ACCESS_TOOLS.filter(([, on]) => on).map(([name]) => name))
  );
  const toggle = (name) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(name)) next.delete(name); else next.add(name);
    return next;
  });
  const count = selected.size;
  return (
    <HeroWindowShell title="Delivery evidence stack" meta="Guided setup · Read-only" className="sw-hero-window--wide">
      <div className="sw-hero-stack-grid">
        {EARLY_ACCESS_TOOLS.map(([name]) => {
          const isOn = selected.has(name);
          return (
            <button
              type="button"
              className={`sw-hero-stack-tool ${isOn ? 'is-selected' : ''}`}
              key={name}
              aria-pressed={isOn}
              onClick={() => toggle(name)}
            >
              <ToolMark name={name}/>
              <span>{name}</span>
              <span className="sw-hero-stack-check" aria-hidden="true">{isOn ? '✓' : ''}</span>
            </button>
          );
        })}
      </div>
      <div className="sw-hero-stack-summary">
        <span><strong>{count} {count === 1 ? 'source' : 'sources'} selected</strong> for a read-only pilot workspace</span>
        <span>Nothing connects until the agency approves access</span>
      </div>
    </HeroWindowShell>
  );
}

const GRAPH_NODES = [
  { id: 'objective', label: 'Improve fleet operations', x: 9, y: 50, tone: 'blue' },
  { id: 'access', label: 'Operator access & SSO', x: 28, y: 18, tone: 'teal' },
  { id: 'console', label: 'Console preferences', x: 28, y: 42, tone: 'amber' },
  { id: 'export', label: 'Manifest export', x: 28, y: 66, tone: 'teal' },
  { id: 'routing', label: 'Alert routing', x: 28, y: 88, tone: 'red' },
  { id: 'release26', label: 'Release v2.6', x: 58, y: 34, tone: 'teal' },
  { id: 'release27', label: 'Release v2.7', x: 58, y: 62, tone: 'teal' },
  { id: 'outcome1', label: 'Coordination reduced', x: 83, y: 30, tone: 'teal' },
  { id: 'outcome2', label: '+21% operator adoption', x: 83, y: 60, tone: 'teal' },
  { id: 'impact', label: '$8,600 expansion', x: 83, y: 88, tone: 'amber' },
];

const GRAPH_LINKS = [
  ['objective', 'access'], ['objective', 'console'], ['objective', 'export'], ['objective', 'routing'],
  ['access', 'release26'], ['console', 'release26'], ['export', 'release27'], ['routing', 'impact'],
  ['release26', 'outcome1'], ['release27', 'outcome2'], ['release27', 'impact'],
];

export function EvidenceGraphHeroVisual() {
  const byId = Object.fromEntries(GRAPH_NODES.map((node) => [node.id, node]));
  return (
    <HeroWindowShell title="Chain of evidence" meta="10 nodes · 11 links" anchorHorizon>
      <div className="sw-hero-graph">
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
          {GRAPH_LINKS.map(([from, to]) => (
            <line
              key={`${from}-${to}`}
              x1={byId[from].x}
              y1={byId[from].y}
              x2={byId[to].x}
              y2={byId[to].y}
              className={to === 'impact' ? 'is-impact' : ''}
            />
          ))}
        </svg>
        {GRAPH_NODES.map((node) => (
          <div
            className={`sw-hero-graph-node is-${node.tone}`}
            style={{ left: `${node.x}%`, top: `${node.y}%` }}
            key={node.id}
          >
            <i aria-hidden="true">{node.id === 'impact' ? '$' : '✓'}</i>
            <span>{node.label}</span>
          </div>
        ))}
      </div>
      <div className="sw-hero-sample-note">Illustrative chain · Colony Ops Console</div>
    </HeroWindowShell>
  );
}

export function ClientQuestionHeroVisual() {
  const copy = tx('heroVisuals.clientQuestions');
  const kpis = [
    { label: copy.effortLabel, value: '+19h', note: copy.effortNote, tone: 'teal' },
    { label: copy.usageLabel, value: '36%', note: copy.usageNote, tone: 'blue' },
    { label: copy.costLabel, value: '$46,600', note: copy.costNote, tone: 'amber' },
  ];
  return (
    <HeroWindowShell title="Active client questions" meta={copy.sampleMeta} anchorHorizon>
      <div className="sw-hero-question-stack">
        <div className="sw-hero-question">
          <div className="sw-hero-widget-kicker"><i/> “Why is this month’s invoice higher than expected?”</div>
          <div className="sw-hero-question-meta">Invoice · Asked in last QBR · April 28</div>
          <div className="sw-hero-question-value">
            <strong>$44,800</strong>
            <span>+24% vs planned · scope expansion</span>
          </div>
          <div className="sw-hero-question-answer">
            <span>Evidence ready</span>
            <strong>13 post-baseline tickets and 5 clarification meetings explain the variance.</strong>
          </div>
        </div>
        <div className="sw-hero-question sw-hero-question--ai">
          <div className="sw-hero-widget-kicker"><i/> “If you’re using AI, why isn’t the work cheaper?”</div>
          <div className="sw-hero-question-meta">AI delivery · Asked in last QBR · April 28</div>
          <div className="sw-hero-question-kpis">
            {kpis.map((kpi) => (
              <div className={`sw-hero-question-kpi is-${kpi.tone}`} key={kpi.label}>
                <span>{kpi.label}</span>
                <strong>{kpi.value}</strong>
                <small>{kpi.note}</small>
              </div>
            ))}
          </div>
          <div className="sw-hero-question-answer">
            <span>ScopeWorth’s answer</span>
            <strong>{copy.answer}</strong>
          </div>
        </div>
      </div>
    </HeroWindowShell>
  );
}

const SAVED_REPORTS = [
  {
    title: 'Halcyon Dynamics · Nexus Program · Apr 2026',
    meta: 'Halcyon Dynamics · Nexus Program · Apr 1–30',
    cadence: 'One-time',
    shared: 'Last shared Jul 23',
  },
  {
    title: 'Farhaven Industries · Colony Ops Console · Mar 2026',
    meta: 'Farhaven Industries · Colony Ops Console · Mar 1–31',
    cadence: 'Weekly · Mon 09:00',
    shared: 'Last shared Jul 21',
    active: true,
  },
  {
    title: 'Meridian Research Group · Lab Operations · Q1 2026',
    meta: 'Meridian Research Group · Lab Operations · Jan–Mar',
    cadence: 'Monthly · day 1 08:00',
    shared: 'Never shared',
  },
];

// =====================================================================
// Security hero — the collection boundary as product UI. The window shows
// what ScopeWorth reads (toggles on) against what it never collects
// (locked rows), staging the page's "delivery proof, not developer
// surveillance" promise before the copy makes it. Direction reference:
// docs/image-references/security.png (ghost-sheet stack + shield badge).
// =====================================================================

export function SavedReportsHeroVisual() {
  return (
    <HeroWindowShell title="Saved reports" meta="Published views">
      <div className="sw-hero-saved-grid">
        {SAVED_REPORTS.map((report) => (
          <article className={`sw-hero-saved-card ${report.active ? 'is-active' : ''}`} key={report.title}>
            <div className="sw-hero-saved-title">
              <strong>{report.title}</strong>
              {report.active ? <span>Active</span> : null}
            </div>
            <p>{report.meta}</p>
            <div className="sw-hero-saved-cadence">{report.cadence}</div>
            <div className="sw-hero-saved-foot">
              <span>{report.shared}</span>
              <span aria-hidden="true">↗ · ⧉ · ⋯</span>
            </div>
          </article>
        ))}
      </div>
      <div className="sw-hero-sample-note">Illustrative saved report library</div>
    </HeroWindowShell>
  );
}
